<admin-app-header></admin-app-header>
<app-enterprise-admin-bar></app-enterprise-admin-bar>

<div class="container-fluid">
    <div class="row header">
        <div class="col-12">
            <h2 class="mb-3">API Management</h2>
            <p class="text-muted">Manage API tokens for external access to enterprise data</p>
        </div>
    </div>

    <!-- No Enterprise Selected Warning -->
    <div class="row mb-4" *ngIf="!enterpriseKey">
        <div class="col-12">
            <div class="alert alert-warning">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Please select an enterprise from the dropdown above to manage API tokens.
            </div>
        </div>
    </div>

    <!-- API Documentation Card -->
    <div class="row mb-4" *ngIf="enterpriseKey">
        <div class="col-12">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0"><i class="fas fa-book me-2"></i>API Documentation</h5>
                </div>
                <div class="card-body">
                    <p>Access the interactive API documentation (Swagger UI) to explore available endpoints.</p>
                    <button class="btn btn-primary" (click)="openApiDocs()">
                        <i class="fas fa-external-link-alt me-2"></i>Open API Documentation
                    </button>
                    <div class="mt-3">
                        <small class="text-muted">
                            <strong>API Base URL:</strong>
                            <code>{{ apiDocsUrl.replace('/docs', '') }}</code>
                        </small>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Create New Token Card -->
    <div class="row mb-4" *ngIf="enterpriseKey">
        <div class="col-12">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0"><i class="fas fa-plus-circle me-2"></i>Create New API Token</h5>
                </div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-4 mb-3">
                            <label for="tokenName" class="form-label">Token Name *</label>
                            <input
                                type="text"
                                class="form-control"
                                id="tokenName"
                                [(ngModel)]="newTokenName"
                                placeholder="e.g., Production API, Testing"
                                [disabled]="isCreating"
                            />
                        </div>
                        <div class="col-md-5 mb-3">
                            <label for="tokenDescription" class="form-label">Description (optional)</label>
                            <input
                                type="text"
                                class="form-control"
                                id="tokenDescription"
                                [(ngModel)]="newTokenDescription"
                                placeholder="e.g., Used by XYZ integration"
                                [disabled]="isCreating"
                            />
                        </div>
                        <div class="col-md-3 mb-3 d-flex align-items-end">
                            <button
                                class="btn btn-success w-100"
                                (click)="createToken()"
                                [disabled]="isCreating || !newTokenName.trim()"
                            >
                                <span *ngIf="isCreating" class="spinner-border spinner-border-sm me-2" role="status"></span>
                                <i *ngIf="!isCreating" class="fas fa-key me-2"></i>
                                {{ isCreating ? 'Creating...' : 'Create Token' }}
                            </button>
                        </div>
                    </div>
                    <div class="alert alert-info mb-0">
                        <i class="fas fa-info-circle me-2"></i>
                        <strong>Important:</strong> After creating a token, you'll only see it once. Make sure to copy and securely store it.
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Newly Created Token Display -->
    <div class="row mb-4" *ngIf="createdPlainToken">
        <div class="col-12">
            <div class="card border-success">
                <div class="card-header bg-success text-white">
                    <h5 class="mb-0"><i class="fas fa-check-circle me-2"></i>New Token Created</h5>
                </div>
                <div class="card-body">
                    <p class="text-danger"><strong>Copy this token now!</strong> It will not be shown again.</p>
                    <div class="input-group">
                        <input
                            type="text"
                            class="form-control font-monospace"
                            [value]="createdPlainToken"
                            readonly
                        />
                        <button class="btn btn-outline-secondary" (click)="copyToClipboard(createdPlainToken)">
                            <i class="fas fa-copy"></i> Copy
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Existing Tokens List -->
    <div class="row mb-4" *ngIf="enterpriseKey">
        <div class="col-12">
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0"><i class="fas fa-list me-2"></i>API Tokens</h5>
                    <span class="badge bg-secondary">{{ tokens.length }} token(s)</span>
                </div>
                <div class="card-body">
                    <!-- Loading State -->
                    <div *ngIf="isLoading" class="text-center py-4">
                        <span class="spinner-border" role="status"></span>
                        <p class="mt-2 text-muted">Loading tokens...</p>
                    </div>

                    <!-- Empty State -->
                    <div *ngIf="!isLoading && tokens.length === 0" class="text-center py-4">
                        <i class="fas fa-key fa-3x text-muted mb-3"></i>
                        <p class="text-muted">No API tokens have been created yet.</p>
                        <p class="text-muted small">Create a token above to get started.</p>
                    </div>

                    <!-- Tokens Table -->
                    <div class="table-responsive" *ngIf="!isLoading && tokens.length > 0">
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Token</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th>Last Used</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let token of tokens" [class.table-secondary]="!token.active">
                                    <td>
                                        <strong>{{ token.name }}</strong>
                                        <br *ngIf="token.description" />
                                        <small *ngIf="token.description" class="text-muted">{{ token.description }}</small>
                                    </td>
                                    <td>
                                        <code>{{ token.tokenPrefix }}...</code>
                                    </td>
                                    <td>
                                        <span *ngIf="token.active" class="badge bg-success">Active</span>
                                        <span *ngIf="!token.active" class="badge bg-danger">Revoked</span>
                                    </td>
                                    <td>
                                        <small>{{ token.createdAt | date:'medium' }}</small>
                                        <br />
                                        <small class="text-muted">by {{ token.createdByEmail }}</small>
                                    </td>
                                    <td>
                                        <small *ngIf="token.lastUsedAt">{{ token.lastUsedAt | date:'medium' }}</small>
                                        <small *ngIf="!token.lastUsedAt" class="text-muted">Never</small>
                                    </td>
                                    <td>
                                        <button
                                            *ngIf="token.active"
                                            class="btn btn-sm btn-outline-danger"
                                            (click)="revokeToken(token)"
                                            title="Revoke token"
                                        >
                                            <i class="fas fa-ban"></i> Revoke
                                        </button>
                                        <span *ngIf="!token.active" class="text-muted">-</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Usage Instructions -->
    <div class="row" *ngIf="enterpriseKey">
        <div class="col-12">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0"><i class="fas fa-question-circle me-2"></i>How to Use</h5>
                </div>
                <div class="card-body">
                    <ol>
                        <li>Create an API token above and securely store it</li>
                        <li>Include the token in your API requests as a Bearer token in the Authorization header:</li>
                    </ol>
                    <pre class="bg-light p-3 rounded"><code>Authorization: Bearer pk_live_your_token_here</code></pre>
                    <h6 class="mt-4">Example Request (curl)</h6>
                    <pre class="bg-dark text-light p-3 rounded"><code>curl -X GET "{{ apiDocsUrl.replace('/docs', '') }}/v1/buildings" \
     -H "Authorization: Bearer pk_live_your_token_here" \
     -H "Content-Type: application/json"</code></pre>
                    <div class="alert alert-warning mt-3 mb-0">
                        <i class="fas fa-shield-alt me-2"></i>
                        <strong>Security:</strong> Keep your API tokens secure. Do not share them in public repositories or client-side code.
                        Tokens provide access to all data within your enterprise.
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
